import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Receipt, Tag, MessageSquare, LifeBuoy, LogOut, ExternalLink, UserCog } from 'lucide-react';
import { useAuth, hardLogout } from '../lib/auth';
import { getAdminStats, listAdminUsers } from '../lib/api';

const C = {
  bg: '#F9FAFB', surface: 'white', border: '#E5E7EB',
  text: '#0A0A0A', subtle: '#6B7280', green: '#0FC862',
};

const CARDS = [
  { key: 'users', label: 'Users', icon: Users },
  { key: 'receipts', label: 'Receipts', icon: Receipt },
  { key: 'categories', label: 'Categories', icon: Tag },
  { key: 'cleo_messages', label: 'Cleo Messages', icon: MessageSquare },
  { key: 'support_tickets', label: 'Support Tickets', icon: LifeBuoy },
];

const ALL_USERS = '__all__';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState('');
  const [users, setUsers] = useState([]);
  const [scope, setScope] = useState(ALL_USERS);

  // Load user roster once.
  useEffect(() => {
    let active = true;
    listAdminUsers()
      .then((rows) => { if (active) setUsers(Array.isArray(rows) ? rows : []); })
      .catch(() => { /* non-fatal — switcher just hides */ });
    return () => { active = false; };
  }, []);

  // Reload stats whenever scope changes.
  useEffect(() => {
    let active = true;
    setStats(null);
    const targetUserId = scope === ALL_USERS ? undefined : scope;
    getAdminStats(targetUserId)
      .then((s) => { if (active) setStats(s); })
      .catch((e) => { if (active) setErr(e?.detail || 'Failed to load stats'); });
    return () => { active = false; };
  }, [scope]);

  const scopedUser = useMemo(
    () => (scope === ALL_USERS ? null : users.find((u) => u.id === scope) || null),
    [scope, users],
  );

  // Hard navigation — avoids the React Router race where RequireAuth /
  // Landing can bounce the user through `/login?redirect=/admin` if the
  // token clear and SPA navigation don't commit in the same render pass.
  const onLogout = () => hardLogout('/');

  // Deep-link into the user-facing app with an admin override. Writes still
  // use the admin's own identity server-side (see services/scoping.py); the
  // override only affects read scoping.
  const viewAsHref = scope === ALL_USERS ? '/app' : `/app?user_id=${encodeURIComponent(scope)}`;

  return (
    <div data-testid="admin-dashboard" style={{ minHeight: '100vh', backgroundColor: C.bg, fontFamily: 'Inter, sans-serif' }}>
      <header style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: C.surface }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: C.green, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>C</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, color: C.text, letterSpacing: '-0.02em' }}>Clasifi Admin</div>
              <div style={{ color: C.subtle, fontSize: 12 }}>Signed in as <strong style={{ color: C.text }}>{user?.name}</strong> ({user?.email})</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Link to={viewAsHref} data-testid="admin-view-as-user" style={{ display: 'inline-flex', gap: 8, alignItems: 'center', padding: '9px 14px', borderRadius: 10, border: `1px solid ${C.border}`, color: C.text, textDecoration: 'none', fontWeight: 600, fontSize: 13 }}>
              <ExternalLink size={14} /> View as user
            </Link>
            <button data-testid="admin-logout" onClick={onLogout} style={{ display: 'inline-flex', gap: 8, alignItems: 'center', padding: '9px 14px', borderRadius: 10, border: `1px solid ${C.border}`, backgroundColor: 'white', color: C.text, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 30, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.02em' }}>
              Welcome back, {user?.name?.split(' ')[0] || 'Admin'}.
            </h1>
            <p style={{ color: C.subtle, marginTop: 6, fontSize: 15 }}>
              {scope === ALL_USERS
                ? "Here's a snapshot of the Clasifi workspace."
                : `Scoped to ${scopedUser?.display_name || scopedUser?.email || scope}.`}
            </p>
          </div>

          {users.length > 0 && (
            <div data-testid="admin-user-switcher-wrap" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12, border: `1px solid ${C.border}`, backgroundColor: C.surface }}>
              <UserCog size={16} color={C.subtle} />
              <label htmlFor="admin-user-switcher" style={{ color: C.subtle, fontSize: 12, fontWeight: 600 }}>View data for</label>
              <select
                id="admin-user-switcher"
                data-testid="admin-user-switcher"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                style={{
                  border: 'none', outline: 'none', background: 'transparent',
                  fontSize: 13, fontWeight: 700, color: C.text, cursor: 'pointer',
                  paddingRight: 4, minWidth: 180,
                }}
              >
                <option value={ALL_USERS}>All users ({users.length})</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {(u.display_name || u.email)} — {u.receipt_count ?? 0} receipts
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {err && (
          <div data-testid="admin-error" style={{ marginTop: 20, padding: 12, border: '1px solid #FECACA', backgroundColor: '#FEF2F2', color: '#DC2626', borderRadius: 10, fontSize: 13 }}>
            {err}
          </div>
        )}

        <div data-testid="admin-stats-grid" style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {CARDS.map(({ key, label, icon: Icon }) => (
            <div key={key} data-testid={`admin-stat-${key}`} style={{ backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: C.subtle }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
                <Icon size={16} />
              </div>
              <div style={{ marginTop: 10, fontSize: 32, fontWeight: 800, color: C.text, letterSpacing: '-0.02em' }}>
                {stats ? stats[key].toLocaleString() : '—'}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
